import React, { useContext, useEffect, useRef } from 'react';
import { FormFieldProps } from 'lib/forms';
import {
  useOnlineScheduling,
  useOnlineSchedulingDispatch,
} from 'lib/forms/OnlineScheduling/OnlineSchedulingContext';
import { FormsContext } from 'lib/forms/FormContext';
import {
  OnlineSchedulingDispatchAction,
  OnlineSchedulingMessage,
} from 'lib/forms/OnlineScheduling/OnlineSchedulingTypes';
import { FormikValues, useFormikContext } from 'formik';
import { formActionFactory } from 'temp/formActionFactory';
import { Feature } from 'src/.generated/Feature.EnterpriseWeb.model';
import { OnlineSchedulingConstants } from 'lib/constants/online-scheduling';
import { sendMessageToParentPage } from 'lib/forms/OnlineScheduling/OnlineSchedulingUtils';
import { getFieldData } from 'lib/forms/FormFieldUtils';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';

export type OSInitialPageLoadProps =
  Feature.EnterpriseWeb.RenewalByAndersen.Forms.Events.OnlineSchedulingInitialPageLoad &
    FormFieldProps;

const OnlineSchedulingInitialPageLoad = (props: OSInitialPageLoadProps): JSX.Element => {
  const { initialValues, setInitialValues } = useContext(FormsContext);
  const onlineSchedulingDispatch = useOnlineSchedulingDispatch();
  const onlineSchedulingContext = useOnlineScheduling();
  const { formProps, pageIndex, updatePageIndex } = useContext(FormsContext);
  const { sitecoreContext } = useSitecoreContext();
  const osDomain = sitecoreContext.osDomain as string;

  const { validateForm, values } = useFormikContext<FormikValues>();
  const elementRef = useRef<HTMLElement>(null);
  const hasPageBeenRendered = useRef(false);

  function initializeLeadData(data: Record<string, unknown>) {
    // create a type for message data
    const dispatchAction: OnlineSchedulingDispatchAction = {
      type: OnlineSchedulingConstants.initialLeadData,
      result: { osDomain: osDomain, leadData: data },
    };
    onlineSchedulingDispatch(dispatchAction);

    const leadData = data.leadData ? (data.leadData as Record<string, string>) : {};
    if (!!data.leadData) {
      setInitialValues((prevState) => {
        return {
          ...prevState,
          ...leadData,
        };
      });
    }
    const messageData = {
      type: OnlineSchedulingConstants.events.onReady,
      description: 'Online Scheduling Experience is ready.',
      pageIndex: pageIndex,
      leadData: leadData,
    };
    sendMessageToParentPage(messageData, osDomain);
  }

  function onlineSchedulingMessageHandler(message: Record<string, unknown>) {
    if (!message?.type) {
      return;
    }
    switch (message.type) {
      case 'initializeLeadData': {
        return initializeLeadData(message.data as Record<string, unknown>);
      }
      case OnlineSchedulingConstants.events.closeModal: {
        const event = new Event(OnlineSchedulingConstants.events.closeModal);
        document.dispatchEvent(event);
      }
      default: {
        return;
      }
    }
  }

  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      process.env.NEXT_PUBLIC_EW_ONLINE_SCHEDULING_DOMAIN?.toLowerCase()
        .split(',')
        .map((url) => url.trim())
        .includes(window.location.origin.toLowerCase())
    ) {
      const parameters = new URLSearchParams(window.location.search);
      const isValidData = parameters.get('isValidData');
      if (isValidData !== 'true') {
        onlineSchedulingDispatch({ type: OnlineSchedulingConstants.setIsLoading, result: false });
      }
      // @ts-ignore add iFrameResizer object to window per https://github.com/davidjbradshaw/iframe-resizer/blob/master/docs/iframed_page/options.md#iframe-page-options
      window.iFrameResizer = {
        targetOrigin: `https://${osDomain}`,
        onMessage: function (message: OnlineSchedulingMessage) {
          onlineSchedulingMessageHandler(message);
        },
        onReady: function () {
          sendMessageToParentPage({ type: 'ready' }, osDomain);
        },
      };
    }
    // only run once on initial load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (onlineSchedulingContext?.isInline) {
      setInitialValues((prevState) => {
        return {
          ...prevState,
          ...(onlineSchedulingContext.primarySubmissionData as unknown as Record<string, string>),
        };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlineSchedulingContext?.isInline]);

  useEffect(() => {
    if (onlineSchedulingContext.isLoading && hasPageBeenRendered.current) {
      try {
        (async () => {
          // validate form
          const errors = await validateForm(initialValues);
          if (Object.keys(errors).length === 0) {
            const submitActions = props.fields?.children;
            if (submitActions?.length > 0) {
              for (let i = 0; i < submitActions.length; i++) {
                const action: FormFieldProps = submitActions[i];
                const actionHandler = formActionFactory(
                  action,
                  values, // move trimStringValues
                  formProps,
                  props,
                  onlineSchedulingContext
                );
                // add error handling
                const result = await actionHandler?.executeAction();
                if (!!result?.result) {
                  onlineSchedulingDispatch(result.result); // after executing the submit action, dispatch an event to the onineSchedulingReducer to update the OnlineSchedulingContext
                  if (result.nextPageIndex && result.nextPageIndex > -1) {
                    updatePageIndex(result.nextPageIndex);
                  } else {
                    updatePageIndex(pageIndex + 1);
                  }
                }
              }
            }
          }
        })().then(() => {
          onlineSchedulingDispatch({ type: OnlineSchedulingConstants.setIsLoading, result: false });
        });
      } catch (e: unknown) {
        console.error(e);
        const messageData = {
          type: OnlineSchedulingConstants.events.onError,
          description: e instanceof Error ? e.message : 'Unknown error',
          pageIndex: pageIndex,
          leadData: getFieldData(formProps, values),
        };
        sendMessageToParentPage(messageData, osDomain);
      }
    }
    hasPageBeenRendered.current = true;
    // only run after the formik values have completely updated
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  return <span ref={elementRef}></span>;
};

export default OnlineSchedulingInitialPageLoad;
